import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type OpenCaseRequest = {
  caseNumber: string;
  nonce: number;
};

type CasePortalContextValue = {
  activeCaseNumber: string | null;
  setActiveCaseNumber: (caseNumber: string | null) => void;
  openCaseRequest: OpenCaseRequest | null;
  openCase: (caseNumber: string) => void;
};

const CasePortalContext = createContext<CasePortalContextValue | null>(null);

const normalizeCaseNumber = (value: string) => value.replace(/\s+/g, "").toUpperCase();

export function CasePortalProvider({ children }: { children: React.ReactNode }) {
  const [activeCaseNumber, setActiveCaseNumber] = useState<string | null>(null);
  const [openCaseRequest, setOpenCaseRequest] = useState<OpenCaseRequest | null>(null);

  const openCase = useCallback((caseNumber: string) => {
    const normalized = normalizeCaseNumber(caseNumber);

    // Update immediately so the sidebar can highlight selection right away.
    setActiveCaseNumber(normalized);

    // Nonce ensures clicking the same case twice still re-opens the dialog flow.
    setOpenCaseRequest((prev) => ({
      caseNumber: normalized,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  }, []);

  const value = useMemo(
    () => ({
      activeCaseNumber,
      setActiveCaseNumber,
      openCaseRequest,
      openCase,
    }),
    [activeCaseNumber, openCaseRequest, openCase]
  );

  return <CasePortalContext.Provider value={value}>{children}</CasePortalContext.Provider>;
}

export function useCasePortal() {
  const ctx = useContext(CasePortalContext);
  if (!ctx) {
    throw new Error("useCasePortal must be used within CasePortalProvider");
  }
  return ctx;
}

